import { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  DashboardIcon,
  OrdersIcon,
  OnlineOrdersIcon,
  CustomersIcon,
  ProductsIcon,
  GrowersIcon,
  GrainVarietiesIcon,
  ApprovalsIcon,
  MailIcon,
  BudgetIcon,
  InvoicesIcon,
  AccountIcon,
  LogoutIcon,
} from './icons';
import './EmployeeNav.css';

const navLinkClass = ({ isActive }) =>
  'employee-nav__link' + (isActive ? ' employee-nav__link--active' : '');

const NAV_ITEMS = [
  { to: '/employee/dashboard',     label: 'Dashboard',     Icon: DashboardIcon },
  { to: '/employee/orders',        label: 'Orders',        Icon: OrdersIcon },
  { to: '/employee/online-orders', label: 'Online Orders', Icon: OnlineOrdersIcon },
  { to: '/employee/invoices',      label: 'Invoices',      Icon: InvoicesIcon },
  { to: '/employee/customers',     label: 'Customers',     Icon: CustomersIcon },
  { to: '/employee/products',      label: 'Products',      Icon: ProductsIcon },
  { to: '/employee/growers',         label: 'Growers',         Icon: GrowersIcon },
  { to: '/employee/grain-varieties', label: 'Grain Varieties', Icon: GrainVarietiesIcon },
  { to: '/employee/account-approvals', label: 'Approvals', Icon: ApprovalsIcon },
  { to: '/employee/mill-emails',       label: 'Mill Emails', Icon: MailIcon },
  { to: '/employee/budget',            label: 'Budget',      Icon: BudgetIcon },
];

const EmployeeNav = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);

  // Close menu whenever the route changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      navigate('/login');
    }
  };

  return (
    <aside className="employee-nav" ref={navRef}>
      {/* ── Brand header ────────────────────────────────── */}
      <div className="employee-nav__brand">
        <span className="employee-nav__brand-mark">DRM</span>
        <div className="employee-nav__brand-text">
          <span className="employee-nav__brand-name">Deep Roots Milling</span>
          <span className="employee-nav__brand-label">Employee Portal</span>
        </div>
      </div>

      {/* ── Sidebar links (desktop) ─────────────────────── */}
      <nav className="employee-nav__links">
        {NAV_ITEMS.map(({ to, label, Icon }) => (
          <NavLink key={to} to={to} className={navLinkClass}>
            <span className="employee-nav__link-icon"><Icon /></span>
            {label}
          </NavLink>
        ))}
      </nav>

      {/* ── Footer (desktop) ────────────────────────────── */}
      <div className="employee-nav__footer">
        {user && (
          <span className="employee-nav__username">{user.email}</span>
        )}
        <button className="employee-nav__account" onClick={() => navigate('/employee/account')}>
          <AccountIcon />
          Account
        </button>
        <button className="employee-nav__logout" onClick={handleLogout}>
          <LogoutIcon />
          Log out
        </button>
      </div>

      {/* ── Mobile top bar ──────────────────────────────── */}
      <div className="employee-nav__mobile-bar">
        <div className="employee-nav__mobile-brand">
          <span className="employee-nav__brand-mark">DRM</span>
          <span className="employee-nav__brand-name">Deep Roots Milling</span>
        </div>
        <button
          className={`employee-nav__hamburger${menuOpen ? ' employee-nav__hamburger--open' : ''}`}
          onClick={() => setMenuOpen(o => !o)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      {/* ── Mobile dropdown ─────────────────────────────── */}
      {menuOpen && (
        <div className="employee-nav__mobile-menu">
          {NAV_ITEMS.map(({ to, label, Icon }) => (
            <NavLink key={to} to={to} className={navLinkClass}>
              <span className="employee-nav__link-icon"><Icon /></span>
              {label}
            </NavLink>
          ))}
          <div className="employee-nav__mobile-divider" />
          {user && (
            <div className="employee-nav__mobile-email">{user.email}</div>
          )}
          <button className="employee-nav__mobile-account" onClick={() => navigate('/employee/account')}>
            Account
          </button>
          <button className="employee-nav__mobile-logout" onClick={handleLogout}>
            Log out
          </button>
        </div>
      )}
    </aside>
  );
};

export default EmployeeNav;
